import { useState } from "react";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { activateTrialForStudent } from "services/api/sessions/sessionsService";
import { getTrialActivationState } from "../utils";
import {
  IUseTrialActivationModal,
  TrialActivationComponent,
  TrialActivationState
} from "./trial-activation-modal.types";
import { useRouter } from "next/router";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import { setLocalStorage } from "services/local-storage";
import { constants } from "constants/constants";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";
import trackEvent from "services/mixpanel/mixpanel";
import * as Sentry from "@sentry/react";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";

const useTrialActivationModal = (
  userToken: string,
  onClose?: () => null
): IUseTrialActivationModal => {
  const [isActivating, setIsActivating] = useBoolean(false);
  const [activationState, setActivationState] =
    useState<TrialActivationState | null>(null);
  const [showTrialActivationComponent, setShowTrialActivationComponent] =
    useState<TrialActivationComponent>(
      TrialActivationComponent.TRIAL_ACTIVATION_MESSAGE
    );

  const router = useRouter();
  const { userSubscription } = useKelasLiveContext();

  const closeTrialModal = () => {
    onClose && onClose();
  };
  const [showTrialActivation, setShowTrialActivation] = useBoolean();

  const handleActivationStates = (activationState: TrialActivationState) => {
    setShowTrialActivationComponent(
      TrialActivationComponent.TRIAL_ACTIVATION_STATUS
    );
    setActivationState(activationState);
    setShowTrialActivation.on();
  };

  const activateTrial = async () => {
    if (userToken) {
      try {
        setIsActivating.on();
        const response = await activateTrialForStudent(userToken);
        const { trialEndDate } = response.data;
        const activationState = getTrialActivationState(
          trialEndDate,
          SubscriptionType.FREE
        );
        setShowTrialActivationComponent(
          TrialActivationComponent.TRIAL_ACTIVATION_STATUS
        );
        setShowTrialActivation.on();
        setActivationState(activationState);
        if (userSubscription.subscriptionStatus) {
          userSubscription?.setSubscriptionStatus({
            ...userSubscription.subscriptionStatus,
            subscriptionType: SubscriptionType.TRIAL
          });
        }
        trackEvent({
          eventName: kelasLiveEvents.liveClassTrialStarted
        });
        return;
      } catch (e) {
        setIsActivating.off();
        Sentry.captureException(e);
        return;
      }
    } else {
      router.push({
        pathname: constants.redirect_to.login,
        query: router.query
      });
      setLocalStorage(constants.KELAS_LIVE_TRIAL_ACTIVATION, {
        activate: true
      });
      return;
    }
  };

  return {
    activationState,
    setActivationState,
    isActivating,
    activateTrial,
    closeTrialModal,
    showTrialActivationComponent,
    setShowTrialActivationComponent,
    handleActivationStates,
    showTrialActivation,
    setShowTrialActivation
  };
};

export { useTrialActivationModal };
